<script setup lang="ts">
import { RBtn } from '@therobotcompany/robco-frontend-library';
import {
  instanceColor,
  type EditorInstance,
} from '@/helpers/LabelingSerialization';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

defineProps<{
  instances: EditorInstance[];
  selectedId: number | null;
  readyCount: number;
  totalCount: number;
  readOnly: boolean;
  rotationEnabled: boolean;
}>();

const emit = defineEmits<{
  select: [id: number];
  remove: [id: number];
  add: [];
}>();

function meta(instance: EditorInstance, rotationEnabled: boolean): string {
  const origin =
    instance.source === 'prelabel' && !instance.touched
      ? $gettext('Model prediction')
      : $gettext('Your annotation');
  return rotationEnabled && instance.angleDeg !== null
    ? `${origin} · ${Math.round(instance.angleDeg)}°`
    : origin;
}
</script>

<template>
  <div class="objects-panel d-flex flex-column">
    <div class="d-flex align-baseline justify-space-between px-3 pt-3 pb-2">
      <h4 class="text-subtitle-2">{{ $gettext('Objects') }}</h4>
      <span class="text-caption text-medium-emphasis">
        {{ instances.length }}
      </span>
    </div>
    <div class="flex-grow-1 overflow-y-auto px-2 pb-2">
      <v-list density="compact" bg-color="transparent" class="pa-0">
        <v-list-item
          v-for="(instance, index) in instances"
          :key="instance.id"
          :active="instance.id === selectedId"
          rounded="lg"
          border
          class="mb-1"
          @click="emit('select', instance.id)"
        >
          <template #prepend>
            <v-icon
              icon="mdi-square-rounded"
              size="small"
              :color="instanceColor(instance.id)"
              class="mr-2"
            />
          </template>
          <v-list-item-title class="text-body-2 font-weight-medium">
            {{ instance.categoryName || $gettext('No class') }}
          </v-list-item-title>
          <v-list-item-subtitle class="text-caption">
            {{ meta(instance, rotationEnabled) }}
          </v-list-item-subtitle>
          <template #append>
            <v-chip
              v-if="instance.source === 'prelabel' && !instance.touched"
              size="x-small"
              color="info"
              density="comfortable"
              class="mr-1"
            >
              {{ $gettext('AI') }}
            </v-chip>
            <span class="text-caption text-medium-emphasis mr-1">
              <!-- List position, not instance.id: ids keep counting after
                   deletes and would show gaps (#1, #3, #4). -->
              #{{ index + 1 }}
            </span>
            <v-btn
              icon="mdi-delete-outline"
              size="x-small"
              variant="text"
              :disabled="readOnly"
              :aria-label="$gettext('Delete object')"
              @click.stop="emit('remove', instance.id)"
            />
          </template>
        </v-list-item>
      </v-list>
      <div
        v-if="instances.length === 0"
        class="text-caption text-medium-emphasis font-italic px-2 py-1"
      >
        {{
          $gettext(
            'No objects yet — draw a mask with AI Segmentation, Brush or Polygon.',
          )
        }}
      </div>
      <r-btn
        variant="outlined"
        icon="mdi-plus"
        :disabled="readOnly"
        class="mt-2 add-object-btn w-100"
        @click="emit('add')"
      >
        {{ $gettext('Add object') }}
      </r-btn>
    </div>
    <v-divider />
    <div class="px-3 py-2">
      <div class="d-flex align-center justify-space-between text-caption mb-1">
        <span class="text-medium-emphasis">{{ $gettext('Labeled') }}</span>
        <span class="font-weight-medium"
          >{{ readyCount }}/{{ totalCount }}</span
        >
      </div>
      <v-progress-linear
        :model-value="totalCount > 0 ? (readyCount / totalCount) * 100 : 0"
        color="primary"
        rounded
        height="6"
      />
    </div>
  </div>
</template>

<style scoped>
.objects-panel {
  width: 220px;
  flex: 0 0 auto;
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  min-height: 0;
}
/* Small displays: the fixed side panels must not squeeze the canvas away. */
@media (max-width: 1280px) {
  .objects-panel {
    width: 180px;
  }
}
.add-object-btn {
  border-style: dashed;
}
</style>
